import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  useFinalPphWindow,
  useGenerateTaxPrep,
  usePphBadan,
  usePpnWorksheet,
  useSetFinalPphWindow,
  useTaxPreps,
} from '../hooks/useOps';
import { formatDate, formatIDR } from '../lib/format';

const PPN_STATUS_COLOR: Record<string, 'error' | 'success' | 'default'> = {
  kurang_bayar: 'error',
  lebih_bayar: 'success',
  nihil: 'default',
};

function ppnStatusLabel(s: string): string {
  return s === 'kurang_bayar' ? 'Kurang bayar' : s === 'lebih_bayar' ? 'Lebih bayar (kredit)' : 'Nihil';
}

export function TaxPrepPage() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [year] = useState(now.getFullYear());
  const [startYear, setStartYear] = useState(String(now.getFullYear()));

  const ppn = usePpnWorksheet(month);
  const pph = usePphBadan(year);
  const windowQ = useFinalPphWindow();
  const setWindow = useSetFinalPphWindow();
  const preps = useTaxPreps();
  const generate = useGenerateTaxPrep();

  return (
    <Box>
      <Typography variant="h5" component="h1" fontWeight={700} mb={1}>
        Persiapan Pajak
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Semua angka di sini adalah <strong>kertas kerja untuk konsultan pajak</strong>. Tidak ada yang dilaporkan ke DJP,
        dan ini bukan nasihat pajak yang mengikat.
      </Alert>

      <Grid container spacing={2}>
        {/* PPN worksheet */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
              <Typography variant="h6" fontWeight={600}>
                PPN Bulanan
              </Typography>
              <Box flexGrow={1} />
              <TextField
                size="small"
                label="Bulan (YYYY-MM)"
                value={month}
                onChange={(e) => setMonth(e.target.value.trim())}
                sx={{ width: 160 }}
              />
            </Stack>
            {ppn.data ? (
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>DPP (penjualan)</TableCell>
                    <TableCell align="right">{formatIDR(ppn.data.outputBase)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>PPN Keluaran</TableCell>
                    <TableCell align="right">{formatIDR(ppn.data.outputTax)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>PPN Masukan (kreditable)</TableCell>
                    <TableCell align="right">({formatIDR(ppn.data.inputTax)})</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Netto</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatIDR(ppn.data.netPayable)}{' '}
                      <Chip
                        size="small"
                        color={PPN_STATUS_COLOR[ppn.data.status]}
                        label={ppnStatusLabel(ppn.data.status)}
                        sx={{ ml: 1 }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Masukkan bulan dalam format YYYY-MM.
              </Typography>
            )}
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => generate.mutate({ taxType: 'ppn', period: month })}
              disabled={generate.isPending || !/^\d{4}-\d{2}$/.test(month)}
            >
              Simpan kertas kerja
            </Button>
          </Paper>
        </Grid>

        {/* PPh Badan / final PPh */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} mb={1}>
              Estimasi PPh Tahunan {year}
            </Typography>
            {pph.data && (
              <>
                <Chip
                  size="small"
                  color={pph.data.regime === 'final_pph_05' ? 'info' : 'default'}
                  label={pph.data.regime === 'final_pph_05' ? 'PPh Final 0,5% (PP 55/2022)' : 'PPh Badan'}
                  sx={{ mb: 1 }}
                />
                <Table size="small">
                  <TableBody>
                    {pph.data.regime === 'final_pph_05' ? (
                      <>
                        <TableRow>
                          <TableCell>Omzet bruto</TableCell>
                          <TableCell align="right">{formatIDR(pph.data.grossTurnover)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Tarif</TableCell>
                          <TableCell align="right">{(Number(pph.data.rate) * 100).toFixed(2)}%</TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <>
                        <TableRow>
                          <TableCell>Laba bersih</TableCell>
                          <TableCell align="right">{formatIDR(pph.data.netProfit)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>PPh bruto ({(Number(pph.data.rate) * 100).toFixed(0)}%)</TableCell>
                          <TableCell align="right">{formatIDR(pph.data.grossTax)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Kredit PPh 23</TableCell>
                          <TableCell align="right">({formatIDR(pph.data.pph23Credit)})</TableCell>
                        </TableRow>
                      </>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Estimasi terutang</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatIDR(pph.data.estimatedPayable)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                {pph.data.note && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {pph.data.note}
                  </Typography>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* Final-PPh window */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1}>
                Window PPh Final 0,5%
              </Typography>
              {windowQ.data ? (
                <Typography variant="body2" mb={1}>
                  Mulai {windowQ.data.startYear}, berakhir {windowQ.data.expiresYear} —{' '}
                  <Chip
                    size="small"
                    color={windowQ.data.active ? 'success' : 'default'}
                    label={windowQ.data.active ? 'Aktif' : 'Sudah lewat — beralih ke PPh Badan'}
                  />
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Belum diset. Masukkan tahun registrasi PT untuk melacak masa berlaku PP 55/2022 (3 tahun).
                </Typography>
              )}
              <Stack direction="row" gap={1}>
                <TextField
                  size="small"
                  label="Tahun mulai"
                  value={startYear}
                  onChange={(e) => setStartYear(e.target.value.trim())}
                  sx={{ width: 130 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setWindow.mutate(startYear)}
                  disabled={setWindow.isPending || !/^\d{4}$/.test(startYear)}
                >
                  Simpan
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Saved worksheets */}
      <Typography variant="h6" fontWeight={600} mt={3} mb={1}>
        Kertas kerja tersimpan
      </Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Jenis</TableCell>
              <TableCell>Periode</TableCell>
              <TableCell align="right">Jumlah</TableCell>
              <TableCell>Dibuat</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(preps.data ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" py={1}>
                    Belum ada kertas kerja tersimpan.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              preps.data!.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.taxType}</TableCell>
                  <TableCell>{p.period}</TableCell>
                  <TableCell align="right">{formatIDR(p.computedAmount)}</TableCell>
                  <TableCell>{formatDate(p.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
